import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";

import { useStateSelector } from "utils/field";
import { codeSort, windowLayerLogic } from "utils/codes";
import { required, maxLength } from "utils/fieldValidation";
import { getAllSubcomponents } from "utils/enclosure/components";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import uDefWindowCodeTemplate from "features/Model/Enclosure/Templates/windowUDef.json";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Field from "components/Field";
import MultiSelect from "components/Input/MultiSelect";
import Button from "components/Button";
import EmptyState from "components/EmptyState";
import Checkbox from "components/Input/Checkbox";
import Tooltip from "components/Tooltip";
import AddLayer from "../AddLayer";
import WindowLegacy from "./WindowLegacy";
import WindowOverall from "./WindowOverall";
import { EmbodiedCarbonCalculationsSummary } from "components/EmbodiedCarbonGrade";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";

import classes from "../style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode } = enclosureActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const descriptionValidation = [maxLength(100)];

const getLayerType = {
    windowLegacy: WindowLegacy,
    windowOverall: WindowOverall,
};

const StandardLayers = React.memo(
    ({
        change,
        componentType,
        fieldAccessor,
        windowAccessor = fieldAccessor,
        isCodeLibrary = false,
        handleClose,
        handleUdefSave,
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        upgradeWhereToAddCode = {},
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { windowUDef = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } = useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } = useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const { windowLegacy: windowLegacyLayers = {}, window: windowOverallLayers = {} } =
            useStateSelector(`${drawerAccessor}.uDefCode.layers`) || {};

        const windowUdefCodeLabel = useStateSelector(`${drawerAccessor}.uDefCode.label`) || "";

        const legacy = Object.keys(windowLegacyLayers).map((key) => ({
            ...windowLegacyLayers[key],
            id: key,
            type: "windowLegacy",
        }));

        const overall = Object.keys(windowOverallLayers).map((key) => ({
            ...windowOverallLayers[key],
            id: key,
            type: "windowOverall",
        }));

        const codeLayers = [...legacy, ...overall];

        const numLayers = codeLayers.length;

        const { codeRef: editingCodeRef = null, isLibCode = false } = windowUDef;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = (windowAccessor || "").includes("fields");

        const availableComponents = getAllSubcomponents("window", components, windowAccessor).sort(codeSort);

        const componentId = windowAccessor.split(".").pop();

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("Window", windowAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"]) ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents) : selectedComponents;

        useEffect(() => {
            const initialValues = {
                uDefCode: isEmpty(windowUDef) ? uDefWindowCodeTemplate : windowUDef,
                addToLibrary: isEmpty(windowUDef) ? true : isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        return (
            <div className={classes.uDefCodeWrapper}>
                {/* <h3>User Defined Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect || (!isCodeLibrary && !isUpgradeComponent) ? "1fr 1fr 1fr" : "1fr 1fr"}  style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.uDefCode.label`}
                        label="User Defined Code Label"
                        placeholder="Enter Code Label"
                        validate={labelValidation}
                        warning={windowUdefCodeLabel?.length >= 15 ? "windowCodeLabelLength" : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.uDefCode.description`}
                        label="Description"
                        placeholder="Enter Code Description"
                        validate={descriptionValidation}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <Field
                                    component={Checkbox}
                                    name={`${drawerAccessor}.addToLibrary`}
                                    label={isLibCode ? "Update code in library" : "Add to code library"}
                                    large
                                    disabled={isLibCode}
                                    type="checkbox"
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                            />
                        </>
                    )}
                </InputRow>
                <div className={`${classes.title} ${classes.layers}`}>
                    <h4>Layers</h4>
                    <div className={classes.layersButtons}>
                        <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                        <AddLayer
                            change={change}
                            numLayers={numLayers}
                            formName={`${drawerAccessor}.uDefCode`}
                            layerLimit={1}
                            options={[
                                {
                                    value: "windowLegacy",
                                    label: "Window Legacy",
                                },
                                {
                                    value: "window",
                                    label: "Window Overall",
                                },
                            ]}
                        />
                    </div>
                </div>
                <div>
                    {isEmpty(codeLayers) && <EmptyState title="No layers to display" style={{}} />}
                    {codeLayers.map((layer, ind) => {
                        const { id, type, ...rest } = layer;
                        const LayerType = getLayerType[type];

                        return (
                            <LayerType
                                type={type}
                                key={ind}
                                id={id}
                                value={rest}
                                change={change}
                                accessor={`${drawerAccessor}.uDefCode.layers.${
                                    type === "windowOverall" ? "window" : type
                                }.${id}`}
                                drawerAccessor={drawerAccessor}
                                barMetric={barMetric}
                            />
                        );
                    })}
                </div>
                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={codeLayers}
                    componentType={"window"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                    isUdefCode={true}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>
                
                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            handleClose();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                windowAccessor,
                                editingCodeRef,
                                uid,
                                componentType,
                                isCodeLibrary,
                                handleUdefSave,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear udef code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "windowUDef")),
    handleSubmit: async ({
        change,
        codeFields,
        windowAccessor,
        editingCodeRef = "",
        uid,
        componentType,
        isCodeLibrary = false,
        handleUdefSave,
        isEditing = false,
        components,
        modelCodes,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const { uDefCode = {}, addToLibrary = false, setAsModelDefault = false, selectedComponents = [] } = codeFields;

        //Close drawer
        handleUdefSave();

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        windowLayerLogic({
            change,
            uDefCode,
            componentType,
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            windowAccessor,
            components,
            modelCodes,
            addToLibrary,
            addToCodeLib,
            uid,
            deleteCode,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardLayers);
